import React, { useEffect, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import ConfigurableValues from "../../config/constants";
import { C } from "../../utils/designTokens";

/**
 * Stripe's card form, on the checkout page itself.
 *
 * This used to live on a separate /stripe screen that the customer landed on
 * AFTER Place Order, because the PaymentIntent needs an order to exist first
 * (its amount and metadata.orderId come from the placed order). Stripe's
 * deferred-intent mode removes that ordering constraint: the Payment Element
 * renders from just an amount and currency, the card is collected here, and
 * checkout places the order, creates the intent and confirms it in one press.
 * The backend creates intents with automatic_payment_methods, which is what
 * this mode requires — no server change was needed.
 */

// One Stripe.js per key for the life of the page — loadStripe on every mount
// would inject the script again each time the customer re-selects Stripe.
const stripePromises = {};
function stripeFor(key) {
  if (!key) return null;
  if (!stripePromises[key]) stripePromises[key] = loadStripe(key);
  return stripePromises[key];
}

// The card fields sit inside Stripe's own frame, so the page's font is loaded
// into it and its fields are drawn like the rest of checkout. Module constants:
// only the amount may change once the form is up.
const STRIPE_FONTS = [{ cssSrc: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" }];
const STRIPE_APPEARANCE = {
  theme: "stripe",
  variables: {
    fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    fontSizeBase: "15px",
    colorPrimary: C.red,
    colorText: C.ink,
    colorTextSecondary: C.muted,
    colorTextPlaceholder: C.muted,
    colorDanger: C.redLink,
    colorBackground: C.bg,
    borderRadius: "12px",
  },
  rules: {
    ".Input": { border: `1px solid ${C.borderStrong}`, boxShadow: "none", padding: "12px" },
    ".Input:focus": { borderColor: C.ink, boxShadow: `0 0 0 1px ${C.ink}` },
    ".Label": { fontSize: "13px", fontWeight: "600", color: C.ink },
  },
};

// Checkout owns Place Order, which sits in a different card from this form, so
// the live stripe + elements instances are handed up through a ref.
function HandleBridge({ handleRef }) {
  const stripe = useStripe();
  const elements = useElements();
  useEffect(() => {
    handleRef.current = stripe && elements ? { stripe, elements } : null;
    return () => {
      handleRef.current = null;
    };
  }, [stripe, elements, handleRef]);
  return null;
}

export default function StripeCardForm({ amount, handleRef }) {
  const { STRIPE_PUBLIC_KEY } = ConfigurableValues();
  const stripePromise = useMemo(() => stripeFor(STRIPE_PUBLIC_KEY), [STRIPE_PUBLIC_KEY]);

  // Whole cents, and never under Stripe's 50-cent floor. This amount is only
  // what wallets like Apple Pay display; the charge is whatever the server
  // prices the placed order at.
  const cents = Math.max(50, Math.round((parseFloat(amount) || 0) * 100));
  const options = useMemo(
    () => ({
      mode: "payment",
      amount: cents,
      // The server creates every order intent in USD (stripe/create-payment-intent).
      currency: "usd",
      fonts: STRIPE_FONTS,
      appearance: STRIPE_APPEARANCE,
    }),
    [cents]
  );

  // The publishable key arrives with the configuration query.
  if (!stripePromise) {
    return (
      <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={20} sx={{ color: C.muted }} />
      </Box>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentElement />
      <HandleBridge handleRef={handleRef} />
    </Elements>
  );
}
